/*
 * Pogi bundle-kit cart producer.
 *
 * The Liquid section owns live variant IDs and localized routes. This module
 * owns only the mutation lifecycle and settles Horizon's standard cart event
 * with a Shopify-normalized cart before preserving the historical /cart handoff.
 */
import { beginPogiCartUpdate } from '@pogi/cart-contract';
import { addPogiCartLines } from '@pogi/cart-mutation';

class PogiBundles extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.bound = false;
  }

  onClick = (event) => {
    const button = event.target.closest('[data-kit-add]');
    if (!button || !this.contains(button) || button.disabled || this.pending) return;

    const items = button.dataset.kitAdd
      .split(',')
      .map((pair) => {
        const [rawId, rawQuantity = '1'] = pair.split(':');
        const id = Number.parseInt(rawId, 10);
        const quantity = Number.parseInt(rawQuantity, 10);
        return { id, quantity };
      })
      .filter((item) => Number.isFinite(item.id) && item.id > 0 && Number.isFinite(item.quantity) && item.quantity > 0);

    if (!items.length) return;
    this.addItems(button, items);
  };

  async addItems(button, items) {
    this.pending = true;
    const originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = this.dataset.addingLabel || 'Adding…';
    this.announce(button.textContent);

    const cartUpdate = beginPogiCartUpdate({
      target: this,
      action: 'add',
      context: 'cart',
      lines: items.map((item) => ({
        merchandiseId: String(item.id),
        quantity: item.quantity,
      })),
      source: 'pogi-bundles',
      detail: { itemCount: items.reduce((sum, item) => sum + item.quantity, 0) },
    });

    try {
      const sections = 'cart-drawer-section';
      const result = await addPogiCartLines({
        cartAddUrl: this.dataset.cartAddUrl,
        cartJsonUrl: this.dataset.cartJsonUrl,
        items,
        requestBody: JSON.stringify({
          items,
          sections,
          sections_url: `${window.location.pathname}${window.location.search}`,
        }),
        requestOptions: {
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        },
      });
      cartUpdate.resolve(result.cart, {
        addedItems: result.addPayload?.items || result.addPayload,
        recovered: result.recovered,
        commitState: result.commitState,
        sections: result.addPayload?.sections,
        suppressAutoOpen: true,
        skipSectionRefresh: true,
      });
      window.location.assign(this.dataset.cartUrl);
    } catch (error) {
      cartUpdate.fail(error, error.currentCart, {
        didError: true,
        commitState: error.commitState,
      });
      this.announce(this.dataset.errorLabel || 'Unable to update your cart. Please try again.');

      if (error.retrySafe) {
        button.disabled = false;
        button.textContent = originalLabel;
      } else {
        /*
         * A timeout or post-success refresh failure has an uncertain/known
         * commit outcome. Never expose an immediate retry that could duplicate
         * the entire kit; hand off to the server-rendered localized cart.
         */
        window.location.assign(this.dataset.cartUrl);
      }
    } finally {
      this.pending = false;
    }
  }

  announce(message) {
    const status = this.querySelector('[data-pogi-bundles-status]');
    if (!status) return;
    status.textContent = '';
    status.hidden = false;
    requestAnimationFrame(() => {
      status.textContent = message;
    });
  }
}

if (!customElements.get('pogi-bundles')) {
  customElements.define('pogi-bundles', PogiBundles);
}
